/**
 * CallPersistentWidget — Mini call bar for the PersistentBar.
 *
 * Renders when the user navigates away from /messenger while a call is active.
 * Shows: conversation name, call timer, mic/camera toggles, end call, return button.
 *
 * This component does NOT use Huddle01 hooks — it only reads from the Zustand store.
 * Media toggles in mock mode are visual-only; in production mode, the actual
 * Huddle01 SDK hooks (inside the messenger route tree) sync with the store.
 *
 * NOTE: When the user navigates away from /messenger, the HuddleProvider unmounts.
 * In DEV_AUTH_BYPASS (mock rooms), this is fine — state lives in Zustand.
 * In production, the Huddle01 connection will drop. The fix is to move
 * HuddleProvider to the shell level — see TODO in routes.tsx.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic,
  MicOff,
  Camera,
  CameraOff,
  PhoneOff,
  ArrowUpRight,
  Phone,
  Video,
} from "lucide-react";
import { cn } from "@slumbr/ui";

import { useMessengerStore } from "../stores/messenger-store";
import { getDisplayName } from "./messenger-layout";

// ─── Call Timer (compact) ────────────────────────────────────────────────────

function MiniCallTimer({ startedAt }: { startedAt: string }) {
  const [elapsed, setElapsed] = useState("0:00");

  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => {
      const diff = Date.now() - start;
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setElapsed(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return <span className="font-mono text-xs text-white/70">{elapsed}</span>;
}

// ─── Main Widget ─────────────────────────────────────────────────────────────

export function CallPersistentWidget() {
  const navigate = useNavigate();
  const activeCall = useMessengerStore((s) => s.activeCall);
  const localCallState = useMessengerStore((s) => s.localCallState);
  const conversations = useMessengerStore((s) => s.conversations);
  const endCall = useMessengerStore((s) => s.endCall);
  const toggleMic = useMessengerStore((s) => s.toggleMic);
  const toggleCamera = useMessengerStore((s) => s.toggleCamera);
  const maximizeCall = useMessengerStore((s) => s.maximizeCall);
  const isCallMinimized = useMessengerStore((s) => s.isCallMinimized);

  // Derive conversation name
  const conversationName = (() => {
    if (!activeCall) return "Call";
    const conversation = conversations.find(
      (c) => c.id === activeCall.conversationId
    );
    return conversation ? getDisplayName(conversation) : "Call";
  })();

  const handleReturnToCall = useCallback(() => {
    if (!activeCall) return;
    // Navigate to the conversation (in case we're outside messenger)
    // and maximize the call overlay
    navigate(`/messenger/${activeCall.conversationId}`);
    maximizeCall();
  }, [activeCall, navigate, maximizeCall]);

  const handleEndCall = useCallback(() => {
    endCall();
  }, [endCall]);

  // Only show when there's an active call AND the overlay is minimized
  if (!activeCall || !isCallMinimized) return null;

  const isActive = activeCall.status === "active";
  const isConnecting =
    activeCall.status === "creating" ||
    activeCall.status === "connecting" ||
    activeCall.status === "ringing" ||
    activeCall.status === "reconnecting";

  return (
    <div className="flex flex-1 items-center gap-2">
      {/* Call type icon + pulse indicator */}
      <div className="relative flex items-center">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500/20">
          {activeCall.type === "video" ? (
            <Video className="h-3.5 w-3.5 text-green-400" />
          ) : (
            <Phone className="h-3.5 w-3.5 text-green-400" />
          )}
        </div>
        {isActive && (
          <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface-1 bg-green-500 dark:border-surface-dark-1" />
        )}
      </div>

      {/* Call info */}
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-xs font-medium text-foreground dark:text-foreground-dark">
          {conversationName}
        </span>
        <div className="flex items-center gap-1.5">
          {isActive && <MiniCallTimer startedAt={activeCall.startedAt} />}
          {isConnecting && (
            <span className="text-[10px] text-yellow-500">Connecting...</span>
          )}
          {isActive && (
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-green-500">Live</span>
            </div>
          )}
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Quick controls */}
      <div className="flex items-center gap-1">
        {/* Mic toggle */}
        <button
          onClick={toggleMic}
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-all",
            localCallState.isMicOn
              ? "bg-surface-2 text-foreground hover:bg-surface-3 dark:bg-surface-dark-2 dark:text-foreground-dark dark:hover:bg-surface-dark-3"
              : "bg-red-500/20 text-red-500 hover:bg-red-500/30"
          )}
          aria-label={localCallState.isMicOn ? "Mute" : "Unmute"}
        >
          {localCallState.isMicOn ? (
            <Mic className="h-3.5 w-3.5" />
          ) : (
            <MicOff className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Camera toggle */}
        <button
          onClick={toggleCamera}
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-all",
            localCallState.isCameraOn
              ? "bg-surface-2 text-foreground hover:bg-surface-3 dark:bg-surface-dark-2 dark:text-foreground-dark dark:hover:bg-surface-dark-3"
              : "bg-red-500/20 text-red-500 hover:bg-red-500/30"
          )}
          aria-label={localCallState.isCameraOn ? "Camera off" : "Camera on"}
        >
          {localCallState.isCameraOn ? (
            <Camera className="h-3.5 w-3.5" />
          ) : (
            <CameraOff className="h-3.5 w-3.5" />
          )}
        </button>

        {/* End call */}
        <button
          onClick={handleEndCall}
          className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white transition-all hover:bg-red-600"
          aria-label="End call"
        >
          <PhoneOff className="h-3.5 w-3.5" />
        </button>

        {/* Return to call */}
        <button
          onClick={handleReturnToCall}
          className="ml-1 flex items-center gap-1 rounded-full bg-brand-500 px-2 py-1 text-[11px] font-medium text-white transition-all hover:bg-brand-600 sm:px-2.5"
          aria-label="Return to call"
        >
          <ArrowUpRight className="h-3 w-3" />
          <span className="hidden sm:inline">Return</span>
        </button>
      </div>
    </div>
  );
}
